import React, { useState, useMemo, memo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import defaultArt from '../assets/images/dj_kip_art.png';
import { usePlay } from '../context/Play';
import { useApi } from '../context/ApiProvider';
import { getImg } from '../utils/getImg';
import {
  useMediaQuery,
  useTheme,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Avatar,
  Link,
  Box,
  Grid,
  IconButton,
  Stack,
  Badge,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  PlayArrow,
  Pause,
  Favorite,
  FavoriteBorder,
  Star,
  StarBorder,
} from '@mui/icons-material';
import ArtistsName from './ArtistsName';
import SponsorsName from './SponsorsName';
import SetMobileCard from './SetMobileCard';
import { useSnackbar } from 'notistack';

// Memoized Avatar component that only re-renders when image URL or id changes
const MemoizedAvatar = memo(
  ({ src, id, sx }) => <Avatar src={src} sx={sx} />,
  (prevProps, nextProps) => {
    return (
      prevProps.src === nextProps.src &&
      prevProps.id === nextProps.id &&
      JSON.stringify(prevProps.sx) === JSON.stringify(nextProps.sx)
    );
  }
);

const SetItem = ({ item, asCard, showGenreLink = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    art_url_xl,
    artist,
    release_date,
    set_name,
    id,
    artists,
    set_sponsors,
    genre,
    track_genres,
    user_fav,
  } = item;
  const [isSetFocus, setIsSetFocus] = useState(false);
  const [isFav, setIsFav] = useState(user_fav?.is_fav);
  const [showLikes, setShowLikes] = useState(user_fav?.show_likes);
  const api = useApi();

  // Use ref to store image URL - this prevents Avatar from re-rendering
  // when SetItem re-renders due to Play context updates
  const setImgRef = useRef(getImg(art_url_xl) || defaultArt);

  // Update ref only when art_url_xl actually changes
  useEffect(() => {
    const newImg = getImg(art_url_xl) || defaultArt;
    if (setImgRef.current !== newImg) {
      setImgRef.current = newImg;
    }
  }, [art_url_xl]);

  // Extract only the values we need from Play context and memoize them
  // This prevents re-renders when playingProgress updates (which happens every second)
  const playContext = usePlay();
  const isPlaying = playContext.isPlaying;
  const playingMetaId = playContext.playingMetaId;
  const audioRef = playContext.audioRef;
  const createAudioRef = playContext.createAudioRef;
  const playPause = playContext.playPause;

  // Memoize isSetPlaying to prevent unnecessary recalculations
  const isSetPlaying = useMemo(
    () => playingMetaId === item.id && isPlaying,
    [playingMetaId, item.id, isPlaying]
  );

  // Memoize sx prop to prevent Avatar from re-rendering
  const avatarSx = useMemo(() => ({ width: 56, height: 56 }), []);

  const releaseDateFormatted = release_date
    ? new Date(release_date).toLocaleDateString('en-US')
    : '';
  let navigate = useNavigate();

  const handlePlayPauseClick = async () => {
    if (isSetPlaying || playingMetaId === item.id) {
      playPause(audioRef);
    } else {
      await createAudioRef(item.id);
    }
  };

  async function handleFavoriteClick() {
    const response = await api.put(`/favs/set/${item.id}`);
    if (response.ok) {
      setIsFav(response.body.is_fav);
      handleLikeNotification(response.body.is_fav, 'favorites');
    }
  }

  async function handleLikeClick() {
    const response = await api.put(`/favs/set/${item.id}/likes-toggle`);
    if (response.ok) {
      setShowLikes(response.body.show_likes);
      handleLikeNotification(response.body.show_likes, 'likes');
    }
  }

  function handleLikeNotification(showAdded, type) {
    if (showAdded) {
      enqueueSnackbar(`Set added to ${type}!`);
    } else {
      enqueueSnackbar(`Set removed from ${type}!`);
    }
  }

  function handleGenreClick(genre) {
    const encodedGenre = encodeURIComponent(genre.trim());
    navigate(`/sets?genre=${encodedGenre}`);
  }

  return (
    <>
      {asCard ? (
        <SetMobileCard item={item} showGenreLink={showGenreLink} />
      ) : (
        <TableRow
          className={isSetPlaying ? 'playing' : ''}
          sx={{
            '.MuiTableCell-root': { padding: 1 },
            ':hover': {
              backgroundColor: 'info.main',
              '& .MuiAvatar-root': {
                borderStyle: 'solid',
                borderColor: 'primary.main',
              },
            },
          }}
          onMouseEnter={() => setIsSetFocus(true)}
          onMouseLeave={() => setIsSetFocus(false)}
        >
          <TableCell component="th" scope="row" sx={{ position: 'relative' }}>
            <MemoizedAvatar src={setImgRef.current} id={id} sx={avatarSx} />
            {isSetFocus && (
              <IconButton
                sx={{ position: 'absolute', top: '12px', left: '10px' }}
                color="primary"
                onClick={handlePlayPauseClick}
                size="large"
              >
                {isSetPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
            )}
          </TableCell>
          <TableCell align="left" sx={{ maxWidth: 200 }}>
            <ArtistsName
              artists={artists || []}
              underline="hover"
              linkColor="primary"
            />
          </TableCell>
          <TableCell align="left" sx={{ maxWidth: 200 }}>
            <Link
              href={''}
              to={`/sets/${id}`}
              underline="hover"
              fontWeight="bold"
            >
              {set_name}
            </Link>
          </TableCell>

          <TableCell align="left" sx={{ maxWidth: 200 }}>
            <SponsorsName
              sponsors={set_sponsors || []}
              linkColor="primary"
              direction="row"
              maxDisplay={2}
              showTruncated={true}
            />
          </TableCell>
          <TableCell align="left">
            {showGenreLink ? (
              <Typography
                color={'primary'}
                sx={{ cursor: 'pointer', fontSize: '1rem' }}
                onClick={() => handleGenreClick(genre)}
              >
                {genre}
              </Typography>
            ) : (
              <>{genre}</>
            )}
          </TableCell>
          <TableCell align="left">{releaseDateFormatted}</TableCell>
          <TableCell align="right">
            <Stack direction="row" alignItems="center">
              <IconButton aria-label="favorite" onClick={handleLikeClick}>
                <Badge
                  color="primary"
                  badgeContent={user_fav?.user_likes}
                  max={999}
                >
                  {showLikes ? <Favorite /> : <FavoriteBorder />}
                </Badge>
              </IconButton>
            </Stack>
          </TableCell>
          <TableCell align="center">
            <IconButton aria-label="favorite" onClick={handleFavoriteClick}>
              {isFav ? <Star /> : <StarBorder />}
            </IconButton>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

SetItem.propTypes = {
  item: PropTypes.object,
  asCard: PropTypes.bool,
  showGenreLink: PropTypes.bool,
};

// Memoize SetItem with custom comparison to prevent re-renders
// Only re-render if item data changed OR if play state for THIS item changed
const MemoizedSetItem = React.memo(SetItem, (prevProps, nextProps) => {
  // Re-render if item data changed
  if (prevProps.item !== nextProps.item) {
    return false; // Props changed, should re-render
  }
  if (prevProps.asCard !== nextProps.asCard) {
    return false; // Props changed, should re-render
  }
  if (prevProps.showGenreLink !== nextProps.showGenreLink) {
    return false; // Props changed, should re-render
  }
  // Props are the same, don't re-render
  // Note: Play context updates will still cause re-renders because SetItem uses usePlay()
  // but this prevents re-renders when props haven't changed
  return true; // Props are equal, skip re-render
});

const SetsTable = ({
  items,
  onTableInteraction,
  tableInteractionConfig,
  hideTableInteraction,
  showGenreLink = true,
}) => {
  const [directionAsc, setDirectionAsc] = useState(false);
  const [activeCol, setActiveCol] = useState(
    hideTableInteraction ? '' : 'release_date'
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTableHeadClick = (selectedColumn) => {
    if (hideTableInteraction) return;
    let dir = directionAsc;
    if (selectedColumn === activeCol) {
      setDirectionAsc(!directionAsc);
      dir = !dir;
    } else {
      setDirectionAsc(true);
      dir = true;
    }
    setActiveCol(selectedColumn);

    let orderBy =
      selectedColumn === 'sponsor'
        ? 'set_sponsors.sponsor'
        : `set_meta.${selectedColumn}`;
    orderBy =
      selectedColumn === 'likes' ? 'user_set_favs.total_user_likes' : orderBy;
    orderBy = selectedColumn === 'favorites' ? 'user_set_favs.is_fav' : orderBy;
    onTableInteraction({
      ...tableInteractionConfig,
      order_by: orderBy,
      order_direction: dir ? 'asc' : 'desc',
    });
  };

  if (!items.length) return <></>;

  return (
    <>
      {isMobile ? (
        <Box sx={{ flexGrow: 1, textAlign: 'left' }} my={3}>
          <Grid container spacing={2}>
            {items.map((item, index) => (
              <Grid item xs={12} lg={3} key={`${index}-${item.id}`}>
                <MemoizedSetItem item={item} asCard />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ overflow: 'visible' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow
                sx={{
                  '.MuiTableCell-root': { padding: '16px 8px' },
                }}
              >
                <TableCell
                  sx={{
                    width: '4%',
                  }}
                ></TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'artist' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '15%',
                  }}
                  onClick={() => handleTableHeadClick('artist')}
                >
                  Artist
                  {activeCol === 'artist' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'set_name' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    color: '#fff',
                    width: '25%',
                  }}
                  onClick={() => handleTableHeadClick('set_name')}
                >
                  Set
                  {activeCol === 'set_name' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'sponsor' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '15%',
                  }}
                  onClick={() => handleTableHeadClick('sponsor')}
                >
                  Curator
                  {activeCol === 'sponsor' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'genre' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '15%',
                  }}
                  onClick={() => handleTableHeadClick('genre')}
                >
                  Genre
                  {activeCol === 'genre' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight:
                      activeCol === 'release_date' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '10%',
                  }}
                  onClick={() => handleTableHeadClick('release_date')}
                >
                  Recorded
                  {activeCol === 'release_date' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    width: '8%',
                    fontWeight: activeCol === 'likes' ? 'bold' : 'normal',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleTableHeadClick('likes')}
                >
                  Likes
                  {activeCol === 'likes' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: activeCol === 'favorites' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '8%',
                  }}
                  onClick={() => handleTableHeadClick('favorites')}
                >
                  Favorites
                  {activeCol === 'favorites' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <MemoizedSetItem
                  item={item}
                  key={`${index}-${item.id}`}
                  showGenreLink={showGenreLink}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

SetsTable.propTypes = {
  items: PropTypes.array,
  onTableInteraction: PropTypes.func,
  tableInteractionConfig: PropTypes.object,
  hideTableInteraction: PropTypes.bool,
  showGenreLink: PropTypes.bool,
};

// Memoize SetsTable to prevent re-renders when items array reference hasn't changed
export default React.memo(SetsTable);
